import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { AmenityReturn } from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading, Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorPage } from '../Components/Custom/ErrorPage';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import { apiErrorStatus } from '../utils/apiErrors';
import AmenityPhoto, { coverPhoto } from './AmenityPhoto';
import BookingPanel from './BookingPanel';
import {
  bookingRules,
  cancellationPolicy,
  feeSummary,
  formatLocalRange,
  hoursSections,
  isBookableMode,
  outOfSeasonText,
  seasonLabel,
  timesAreIn,
  todayIn,
  windowsOn,
} from './reservationFormat';
import { bookingBlockMessage, reservationErrorMessage } from './ruleMessages';
import {
  depositPolicyText,
  documentRequirementLines,
  guestPricingText,
  priceRuleText,
} from './reservationPhase3';
import WaitlistJoinPanel from './WaitlistJoinPanel';

const card =
  'rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800';

/**
 * One amenity: its rules, hours, closures and price in words, and — if it
 * takes bookings — the booking panel (#643). On a phone the panel comes
 * after the rules, so a homeowner reads what they are agreeing to first.
 */
const AmenityDetailPage: React.FC = () => {
  const { amenityId } = useParams<{ amenityId: string }>();
  const { selectedHome, homes, loading: memberLoading } = useMember();
  const homeId = selectedHome?.home_id;
  const [amenity, setAmenity] = useState<AmenityReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by "Try again" to re-run the load below.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!homeId || !amenityId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    memberReservationService
      .getMyAmenity({ homeId, amenityId })
      .then(({ data }) => {
        if (!cancelled) setAmenity(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setAmenity(null);
        // A 404 here is usually the switcher: the selected home is in a
        // different association from this amenity.
        setError(
          apiErrorStatus(err) === 404
            ? homes.length > 1
              ? "This amenity isn't available to the home you've selected."
              : "We couldn't find that amenity."
            : reservationErrorMessage(err, "Couldn't load this amenity.")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId, amenityId, homes.length, reloadKey]);

  if (memberLoading) return <LoadingSpinner />;

  const blockedBecause = amenity ? bookingBlockMessage(amenity) : null;
  const bookable = !!amenity && isBookableMode(amenity.booking_mode) && !blockedBecause;
  const today = amenity ? todayIn(amenity.timezone) : '';
  const closedForSeason = !!amenity && windowsOn(amenity, today).outOfSeason;
  const overnight = amenity?.booking_mode === 'overnight';
  const documentLines = amenity ? documentRequirementLines(amenity) : [];

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="amenities" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Link
        to="/amenities"
        className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:underline dark:text-zinc-400"
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
        Amenities
      </Link>

      {homes.length > 1 && (
        <div className="mt-2">
          <HomeSelector />
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : error || !amenity || !homeId ? (
        <ErrorPage
          title="Couldn't load this amenity"
          message={error ?? "We couldn't find that amenity."}
          onRetry={error ? () => setReloadKey((k) => k + 1) : undefined}
        />
      ) : (
        <>
          <div className="mt-4 overflow-hidden rounded-2xl">
            <AmenityPhoto
              photo={coverPhoto(amenity.photos)}
              alt={amenity.name}
              className="h-48 sm:h-64"
            />
          </div>

          <Heading className="mt-4">{amenity.name}</Heading>
          {amenity.location && <Text className="mt-1">{amenity.location}</Text>}
          {amenity.description && (
            <Text className="mt-3 whitespace-pre-wrap">{amenity.description}</Text>
          )}

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="space-y-6 lg:col-span-3">
              {amenity.booking_mode !== 'info_only' && (
                <section className={card}>
                  <Subheading>Price and cancellation</Subheading>
                  <Text className="mt-2">{feeSummary(amenity)}</Text>
                  {(amenity.price_rules ?? []).length > 0 && (
                    <ul className="mt-2 space-y-1 text-sm text-zinc-700 dark:text-zinc-300">
                      {(amenity.price_rules ?? []).map((rule) => (
                        <li key={rule.id}>{priceRuleText(rule)}</li>
                      ))}
                    </ul>
                  )}
                  {guestPricingText(amenity) && (
                    <Text className="mt-1">{guestPricingText(amenity)}</Text>
                  )}
                  {depositPolicyText(amenity) && (
                    <Text className="mt-1">{depositPolicyText(amenity)}</Text>
                  )}
                  <Text className="mt-1">{cancellationPolicy(amenity)}</Text>
                </section>
              )}

              {amenity.group_id && amenity.group_name && bookable && (
                <section className={card}>
                  <Subheading>Part of {amenity.group_name}</Subheading>
                  <Text className="mt-2">
                    Any of them will do? Book whichever is free at the time you want.
                  </Text>
                  <Link
                    to={`/amenities/groups/${amenity.group_id}`}
                    className="mt-2 inline-block text-sm font-medium text-sky-700 hover:underline dark:text-sky-400"
                  >
                    Book any of {amenity.group_name}
                  </Link>
                </section>
              )}

              {documentLines.length > 0 && amenity.booking_mode !== 'info_only' && (
                <section className={card}>
                  <Subheading>Documents needed</Subheading>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
                    {documentLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <Text className="mt-2 text-sm">
                    You upload them from your booking. It's approved once your management company
                    accepts them.
                  </Text>
                </section>
              )}

              {closedForSeason && (
                <Text className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                  {outOfSeasonText(amenity, today)}
                </Text>
              )}

              {overnight ? (
                (amenity.seasons ?? []).length > 0 && (
                  <section className={card}>
                    <Subheading>Open</Subheading>
                    <ul className="mt-2 space-y-1 text-sm text-zinc-950 dark:text-white">
                      {(amenity.seasons ?? []).map((s) => (
                        <li key={s.id}>{seasonLabel(s)}</li>
                      ))}
                    </ul>
                  </section>
                )
              ) : (
                <section className={card}>
                  <Subheading>Hours</Subheading>
                  {hoursSections(amenity).map((section) => (
                    <div key={section.title ?? 'all'} className="mt-2">
                      {section.title && (
                        <div className="mb-1 text-sm font-medium text-zinc-950 dark:text-white">
                          {section.title}
                        </div>
                      )}
                      {section.allDay ? (
                        <Text>Open all day, every day.</Text>
                      ) : (
                        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
                          {section.lines.map((line) => (
                            <React.Fragment key={line.weekday}>
                              <dt className="text-zinc-500 dark:text-zinc-400">{line.label}</dt>
                              <dd className="text-zinc-950 dark:text-white">{line.text}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      )}
                    </div>
                  ))}
                  {(amenity.seasons ?? []).length > 0 &&
                    !amenity.hours.some((w) => !w.season_id) && (
                      <Text className="mt-2 text-sm">Closed outside these seasons.</Text>
                    )}
                  <Text className="mt-2 text-xs">{timesAreIn(amenity.timezone)}</Text>
                </section>
              )}

              {amenity.upcoming_closures.length > 0 && (
                <section className={card}>
                  <Subheading>Upcoming closures</Subheading>
                  <ul className="mt-2 space-y-2 text-sm">
                    {amenity.upcoming_closures.map((c) => (
                      <li key={c.id}>
                        <div className="text-zinc-950 dark:text-white">
                          {formatLocalRange(c.starts_local, c.ends_local)}
                        </div>
                        <div className="text-zinc-500 dark:text-zinc-400">{c.reason}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {amenity.booking_mode !== 'info_only' && (
                <section className={card}>
                  <Subheading>Booking rules</Subheading>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-zinc-700 dark:text-zinc-300">
                    {bookingRules(amenity).map((rule) => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ul>
                </section>
              )}

              {amenity.rules_text && (
                <section className={card}>
                  <Subheading>House rules</Subheading>
                  <Text className="mt-2 whitespace-pre-wrap">{amenity.rules_text}</Text>
                </section>
              )}
            </div>

            <div className="space-y-6 lg:col-span-2">
              {bookable ? (
                <>
                  <BookingPanel amenity={amenity} homeId={homeId} />
                  {!overnight && <WaitlistJoinPanel amenity={amenity} homeId={homeId} />}
                </>
              ) : (
                <section className={card}>
                  <Subheading>
                    {amenity.booking_mode === 'info_only'
                      ? 'No booking needed'
                      : blockedBecause
                        ? "You can't book this"
                        : 'Booking through management'}
                  </Subheading>
                  <Text className="mt-2">
                    {amenity.booking_mode === 'info_only'
                      ? `${amenity.name} is open to every household during its hours.`
                      : blockedBecause ??
                        `${amenity.name} can't be booked online yet. Please contact your management company.`}
                  </Text>
                </section>
              )}
            </div>
          </div>
        </>
      )}
    </SidebarLayout>
  );
};

export default AmenityDetailPage;
